import { apiService } from '@/services/ApiService';
import lookupDataService from '@/services/data/LookupDataService';
import { refreshOutline } from 'ionicons/icons';
import { SettingsFieldConfig, SettingsModalSecondaryAction } from '@/types/model/settingsModal';
import { UserPreferencesRequest } from '@/types/request/userPreferencesRequest';
import { UserPreferencesResponse } from '@/types/response/userResponses';

// ---- Email ----

export interface EmailForm {
    email: string;
}

export const emailEmptyForm: EmailForm = { email: '' };

export const emailInitialData = (currentEmail: string): EmailForm => ({ email: currentEmail });

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const emailFields: SettingsFieldConfig<EmailForm>[] = [
    {
        key: 'email', label: 'Email Address', type: 'text', placeholder: 'your@email.com',
        validate: v => {
            const email = String(v).trim();
            if (!email) return 'Email is required';
            if (!EMAIL_REGEX.test(email)) return 'Please enter a valid email address';
            return undefined;
        },
    },
];

export const saveEmail = (onEmailSaved: (email: string) => void) => async (form: EmailForm): Promise<void> => {
    await apiService.updateEmailAddress(form.email.trim());
    onEmailSaved(form.email.trim());
};

// ---- Defaults ----

export interface DefaultsForm {
    startupPage: string;
    groupIds: string[];
    siteIds: string[];
    areaIds: string[];
    weight: string;
    interval: string;
    summaryDashboardDays: string;
    statusDashboardDays: string;
    inputDelayHours: string;
    repeatCorrectiveActionCount: string;
    checklistScore: string;
    toDoDashboardDays: string;
    inspectionResultsDays: string;
    correctiveActionAlert: boolean;
    outstandingInspectionAlert: boolean;
    lowChecklistScoreAlert: boolean;
    lowChecklistScore: string;
    readyForApprovalAlert: boolean;
    flaggedAsExceptionalAlert: boolean;
}

export const defaultsEmptyForm: DefaultsForm = {
    startupPage: 'summary', groupIds: [], siteIds: [], areaIds: [], weight: 'primary', interval: 'monthly',
    summaryDashboardDays: '', statusDashboardDays: '', inputDelayHours: '', repeatCorrectiveActionCount: '',
    checklistScore: '', toDoDashboardDays: '', inspectionResultsDays: '', correctiveActionAlert: false,
    outstandingInspectionAlert: false, lowChecklistScoreAlert: false, lowChecklistScore: '',
    readyForApprovalAlert: false, flaggedAsExceptionalAlert: false,
};

export const defaultsInitialData = (preferences: UserPreferencesResponse | null): DefaultsForm => {
    if (!preferences) return defaultsEmptyForm;

    return {
        startupPage: preferences.StartupPage ?? 'summary',
        groupIds: (preferences.GroupIds ?? []).map(String),
        siteIds: (preferences.SiteIds ?? []).map(String),
        areaIds: (preferences.AreaIds ?? []).map(String),
        weight: preferences.Weight ?? 'primary',
        interval: preferences.Interval ?? 'monthly',
        summaryDashboardDays: String(preferences.SummaryDashboardDays ?? ''),
        statusDashboardDays: String(preferences.StatusDashboardDays ?? ''),
        inputDelayHours: String(preferences.InputDelayHours ?? ''),
        repeatCorrectiveActionCount: String(preferences.RepeatCorrectiveActionCount ?? ''),
        checklistScore: String(preferences.ChecklistScore ?? ''),
        toDoDashboardDays: String(preferences.ToDoDashboardDays ?? ''),
        inspectionResultsDays: String(preferences.InspectionResultsDays ?? ''),
        correctiveActionAlert: preferences.CorrectiveActionAlert ?? false,
        outstandingInspectionAlert: preferences.OutstandingInspectionAlert ?? false,
        lowChecklistScoreAlert: preferences.LowChecklistScoreAlert ?? false,
        lowChecklistScore: String(preferences.LowChecklistScore ?? ''),
        readyForApprovalAlert: preferences.ReadyForApprovalAlert ?? false,
        flaggedAsExceptionalAlert: preferences.FlaggedAsExceptionalAlert ?? false,
    };
};

const positiveInteger = (value: unknown): string | undefined => {
    const parsed = Number(String(value));
    return Number.isInteger(parsed) && parsed > 0 ? undefined : 'Must be a positive integer';
};

const nonNegativeNumber = (value: unknown): string | undefined => {
    const parsed = Number(String(value));
    return String(value).trim() && !Number.isNaN(parsed) && parsed >= 0 ? undefined : 'Must be a non-negative number';
};

export const defaultsFields = (): SettingsFieldConfig<DefaultsForm>[] => {
    const groups = Array.from(lookupDataService.getGroups()?.values() ?? []);
    const sites = Array.from(lookupDataService.getSites()?.values() ?? []).filter(site => site.IsActive);
    const areas = Array.from(lookupDataService.getExpertiseAreas()?.values() ?? []);

    return [
        { key: 'startupPage', label: 'Start-up Page', type: 'select', section: 'General', options: [
            { value: 'summary', label: 'Summary Dashboard' }, { value: 'status', label: 'Status Dashboard' },
            { value: 'to-do', label: 'To Do Dashboard' }, { value: 'line-items', label: 'Line Items' },
            { value: 'inspection-plans', label: 'Inspection Plans' }, { value: 'inspection-schedule', label: 'Inspection Schedule' },
            { value: 'inspection-results', label: 'Inspection Results' }, { value: 'corrective-actions', label: 'Corrective Actions' },
            { value: 'inspection-analyst', label: 'Inspection Analyst' },
        ] },
        { key: 'groupIds', label: 'Group', type: 'multiselect', section: 'General', options: groups.map(group => ({ value: String(group.Id), label: group.Name })) },
        { key: 'siteIds', label: 'Site', type: 'multiselect', section: 'General', options: sites.map(site => ({ value: String(site.Id), label: site.Name })) },
        { key: 'areaIds', label: 'Area of Expertise', type: 'multiselect', section: 'General', options: areas.map(area => ({ value: String(area.Id), label: area.Name })) },
        { key: 'weight', label: 'Line Item Weight', type: 'select', section: 'Summary Dashboard & Inspection Analyst', options: [{ value: 'primary', label: 'Primary' }, { value: 'alternate', label: 'Alternate' }] },
        { key: 'interval', label: 'Interval', type: 'select', section: 'Summary Dashboard & Inspection Analyst', options: [{ value: 'weekly', label: 'Weekly' }, { value: 'monthly', label: 'Monthly' }, { value: 'quarterly', label: 'Quarterly' }, { value: 'yearly', label: 'Yearly' }] },
        { key: 'summaryDashboardDays', label: 'Generate for the past x days', type: 'number', section: 'Summary Dashboard & Inspection Analyst', validate: positiveInteger },
        { key: 'statusDashboardDays', label: 'Generate for the past x days', type: 'number', section: 'Status Dashboard', validate: positiveInteger },
        { key: 'inputDelayHours', label: 'Input delay >= x hours', type: 'number', section: 'Status Dashboard', helperText: 'Inspection Results with an input delay at or above this threshold', validate: positiveInteger },
        { key: 'repeatCorrectiveActionCount', label: 'Minimum Repeat CA Count', type: 'number', section: 'Status Dashboard', validate: positiveInteger },
        { key: 'checklistScore', label: 'Checklist Score <= x (no CA created)', type: 'number', section: 'Status Dashboard', validate: nonNegativeNumber },
        { key: 'toDoDashboardDays', label: 'Generate for the past x days', type: 'number', section: 'To Do Dashboard', validate: positiveInteger },
        { key: 'inspectionResultsDays', label: 'Generate for the past x days', type: 'number', section: 'Inspection Results', validate: positiveInteger },
        { key: 'correctiveActionAlert', label: 'CA Status Change Alert', type: 'checkbox', section: 'Email Alerts' },
        { key: 'outstandingInspectionAlert', label: 'Outstanding Inspection Alert', type: 'checkbox', section: 'Email Alerts' },
        { key: 'lowChecklistScoreAlert', label: 'Low Checklist Score Alert', type: 'checkbox', section: 'Email Alerts' },
        { key: 'lowChecklistScore', label: 'when Checklist Score <= x', type: 'number', section: 'Email Alerts', visible: form => form.lowChecklistScoreAlert, validate: nonNegativeNumber },
        { key: 'readyForApprovalAlert', label: 'Inspection Ready for Approval Alert', type: 'checkbox', section: 'Email Alerts' },
        { key: 'flaggedAsExceptionalAlert', label: 'Inspection Flagged as Exceptional Alert', type: 'checkbox', section: 'Email Alerts' },
    ];
};

export const saveDefaults = async (form: DefaultsForm): Promise<void> => {
    const request: UserPreferencesRequest = {
        startupPage: form.startupPage,
        groupIds: form.groupIds.length > 0 ? form.groupIds.map(Number) : null,
        siteIds: form.siteIds.length > 0 ? form.siteIds.map(Number) : null,
        areaIds: form.areaIds.length > 0 ? form.areaIds.map(Number) : null,
        weight: form.weight, interval: form.interval, summaryDashboardDays: form.summaryDashboardDays,
        statusDashboardDays: form.statusDashboardDays, inputDelayHours: form.inputDelayHours,
        repeatCorrectiveActionCount: form.repeatCorrectiveActionCount, checklistScore: Number(form.checklistScore),
        toDoDashboardDays: form.toDoDashboardDays, inspectionResultsDays: form.inspectionResultsDays,
        correctiveActionAlert: form.correctiveActionAlert, outstandingInspectionAlert: form.outstandingInspectionAlert,
        lowChecklistScoreAlert: form.lowChecklistScoreAlert, lowChecklistScore: Number(form.lowChecklistScore),
        readyForApprovalAlert: form.readyForApprovalAlert, flaggedAsExceptionalAlert: form.flaggedAsExceptionalAlert,
    };
    await apiService.saveUserPreferences(request);
};

export const resetDefaultsAction: SettingsModalSecondaryAction = {
    label: 'Reset to Defaults',
    icon: refreshOutline,
    confirmHeader: 'Reset to Defaults',
    confirmMessage: 'This will reset all your preferences to system defaults. This action cannot be undone.',
    confirmLabel: 'Reset',
    successMessage: 'Preferences reset to defaults.',
    errorMessage: 'Failed to reset preferences. Please try again.',
    dismissOnSuccess: true,
    onExecute: () => apiService.resetUserPreferences(),
};
